import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Icon,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import toast, { Toaster } from "react-hot-toast";
import PatientHeader from "./PatientHeader";
import FirstStage from "./PatientSheetFirstStage";
import SecondStage from "./PatientProgramSecoundStage";
import { useParams } from "react-router-dom";

const steps = ["Physical Diagnosis", "Patient Program"];

const PatientSheetDialog = ({ open, onClose, patient, initialStep }) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  React.useEffect(() => {
    if (open) {
      setActiveStep(initialStep);
    }
  }, [open, initialStep]);
  const stageRef = useRef();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isLastStep = activeStep === steps.length - 1;

  const handleNext = async () => {
    if (stageRef.current && stageRef.current.submit) {
      setIsSubmitting(true);
      const success = await stageRef.current.submit();
      setIsSubmitting(false);
      if (!success) return;
    }

    if (isLastStep) {
      onClose();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleSkip = () => {
    if (isLastStep) {
      onClose();
    } else {
      toast("Stage skipped", { icon: "↪️" });
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="2xl"
      fullWidth={true}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          height: isMobile ? "100%" : "90vh",
        },
      }}
    >
      <Toaster />
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDBox>
            <MDTypography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Patient Clinical Sheet
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
            </MDTypography>
          </MDBox>
          <IconButton onClick={onClose} disabled={isSubmitting} edge="end">
            <CloseIcon />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: isMobile ? 2 : 3,
          backgroundColor: "#f8f9fa",
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ mb: 4 }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <MDBox
          sx={{
            backgroundColor: "#fff",
            borderRadius: 2,
            p: isMobile ? 2 : 3,
            boxShadow: theme.shadows[1],
          }}
        >
          <PatientHeader patient={patient} />

          <MDBox mt={3}>
            {activeStep === 0 && (
              <FirstStage
                key={`stage-0-${open}`}
                ref={stageRef}
                patientId={id}
              />
            )}
            {activeStep === 1 && (
              <SecondStage
                key={`stage-1-${open}`}
                ref={stageRef}
                patientId={id}
                contractId={patient?.getPatient?.contractId}
              />
            )}
          </MDBox>
        </MDBox>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          display: "flex",
          flexDirection: isMobile ? "column-reverse" : "row",
          gap: 2,
          justifyContent: "space-between",
        }}
      >
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={activeStep === 0 ? onClose : handleBack}
          disabled={isSubmitting}
          fullWidth={isMobile}
        >
          {activeStep === 0 ? "Cancel" : "Back"}
        </MDButton>

        <MDBox
          display="flex"
          gap={1}
          flexDirection={isMobile ? "column" : "row"}
          sx={{ width: isMobile ? "100%" : "auto" }}
        >
          <MDButton
            variant="text"
            color="info"
            onClick={handleSkip}
            disabled={isSubmitting}
            fullWidth={isMobile}
          >
            {isLastStep ? "Skip & Finish" : "Skip Stage"}
          </MDButton>

          <MDButton
            variant="gradient"
            color="info"
            onClick={handleNext}
            disabled={isSubmitting}
            fullWidth={isMobile}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                {isLastStep ? "Save & Finish" : "Next Stage"}
                <Icon sx={{ ml: 1 }}>
                  {isLastStep ? "check" : "chevron_right"}
                </Icon>
              </>
            )}
          </MDButton>
        </MDBox>
      </DialogActions>
    </Dialog>
  );
};

export default PatientSheetDialog;
